import { WordRepeat, WordLayoutPart, WordPlaceholderChanges } from 'src/services/presentations/presentations.types';

// The placeholder editor's preview (staging feedback, JT1-1887). It writes the changes into the
// document's text the same way the server writes them into the file, so what the admin sees is
// what they get: a repeat's own placeholders first, then the document-wide ones, every match, and
// never inside a placeholder already there.

/** What each list's loop is called in the file, as the server writes it. */
export const LOOP_VARIABLES: Record<string, string> = {
  employment: 'role',
  education: 'study',
  languages: 'language',
  sections: 'section',
  skills: 'skill',
  certifications: 'certification',
};

/** Where `find` starts in `text`. A match can take in a whole {…}, such as a note, but never part of one. */
export function occurrences(text: string, find: string): number[] {
  if (!find) return [];
  const braces = Array.from(text.matchAll(/\{[^{}]*\}/g)).map((m) => [m.index ?? 0, (m.index ?? 0) + m[0].length]);
  const found: number[] = [];
  let from = 0;
  for (let at = text.indexOf(find, from); at >= 0; at = text.indexOf(find, from)) {
    const end = at + find.length;
    if (braces.some(([start, stop]) => at < stop && end > start && (at > start || end < stop))) {
      from = at + 1;
    } else {
      found.push(at);
      from = end;
    }
  }
  return found;
}

/**
 * The selected text, grown to take in any {…} it starts or ends inside, so selecting part of a note
 * such as {provide an example} replaces the whole note, braces and all.
 */
export function expandToBraces(text: string, find: string): string {
  const at = text.indexOf(find);
  if (!find || at < 0) return find;
  let start = at;
  let end = at + find.length;
  Array.from(text.matchAll(/\{[^{}]*\}/g)).forEach((m) => {
    const from = m.index ?? 0;
    const to = from + m[0].length;
    if (start < to && end > from) {
      start = Math.min(start, from);
      end = Math.max(end, to);
    }
  });
  return text.slice(start, end);
}

/** The placeholder as it's written into the file: {candidate.full_name}, or {$role.employer} in a repeat. */
export function placeholderText(path: string, repeat?: Pick<WordRepeat, 'list'>): string {
  if (repeat) {
    const variable = LOOP_VARIABLES[repeat.list];
    if (path === repeat.list) return `{$${variable}}`;
    const [list, field] = path.split('.');
    if (list === repeat.list && field) return `{$${variable}.${field}}`;
  }
  return `{${path}}`;
}

/** Where a body paragraph sits: in a table cell (and whether it's the cell's first paragraph), or not. */
export type ParagraphPlace = { table?: number; row?: number; cell?: number; first?: boolean };

export function bodyParagraphs(parts: WordLayoutPart[]): Map<number, ParagraphPlace> {
  const where = new Map<number, ParagraphPlace>();
  (parts.find((p) => p.kind === 'body')?.items ?? []).forEach((item) => {
    if (item.type === 'paragraph') where.set(item.index, {});
    else item.rows.forEach((row) => row.cells.forEach((cell, c) => cell.forEach((p, i) => where.set(p.index, { table: item.index, row: row.index, cell: c, first: i === 0 }))));
  });
  return where;
}

/** The body paragraphs a repeat covers. */
export function coveredBy(repeat: WordRepeat, where: Map<number, { table?: number; row?: number }>): number[] {
  return Array.from(where.entries())
    .filter(([index, at]) => (repeat.table !== undefined ? at.table === repeat.table && at.row === repeat.row : index >= (repeat.from ?? 0) && index <= (repeat.to ?? -1)))
    .map(([index]) => index);
}

/** The repeat a body paragraph is in, if any. */
export function repeatOf(changes: WordPlaceholderChanges, where: Map<number, { table?: number; row?: number }>, part: string, index: number): number {
  if (part !== 'body') return -1;
  return changes.repeats.findIndex((repeat) => coveredBy(repeat, where).includes(index));
}

export interface Preview {
  /** Each paragraph's text with the changes written in, keyed `${part}:${index}`. */
  texts: Map<string, string>;
  /** How many places each change replaces: `g${i}` for document-wide ones, `r${repeat}.${i}` inside a repeat. */
  counts: Map<string, number>;
}

/** Every paragraph's text with the changes written in, and how many places each change hits. */
export function previewChanges(parts: WordLayoutPart[], changes: WordPlaceholderChanges): Preview {
  const where = bodyParagraphs(parts);
  const texts = new Map<string, string>();
  const counts = new Map<string, number>();
  const tally = (key: string, n: number) => counts.set(key, (counts.get(key) ?? 0) + n);
  changes.replacements.forEach((_, i) => counts.set(`g${i}`, 0));
  changes.repeats.forEach((repeat, r) => repeat.replacements.forEach((_, i) => counts.set(`r${r}.${i}`, 0)));

  const write = (part: string, index: number, original: string) => {
    const r = repeatOf(changes, where, part, index);
    const steps = [
      ...(r >= 0 ? changes.repeats[r].replacements.map((c, i) => ({ key: `r${r}.${i}`, find: c.find, insert: placeholderText(c.placeholder, changes.repeats[r]) })) : []),
      ...changes.replacements.map((c, i) => ({ key: `g${i}`, find: c.find, insert: placeholderText(c.placeholder) })),
    ];
    const text = steps.reduce((current, step) => {
      const starts = occurrences(current, step.find);
      tally(step.key, starts.length);
      // From the last match back, so the earlier matches' positions still hold.
      return starts.reduceRight((written, start) => written.slice(0, start) + step.insert + written.slice(start + step.find.length), current);
    }, original);
    // A repeated row's field for this cell takes the cell's first paragraph, as the server writes it.
    const at = part === 'body' ? where.get(index) : undefined;
    const field = r >= 0 && at?.first && at.cell !== undefined ? changes.repeats[r].columns?.[at.cell] : null;
    texts.set(`${part}:${index}`, field ? placeholderText(field, changes.repeats[r]) : text);
  };

  parts.forEach((part) => part.items.forEach((item) => {
    if (item.type === 'paragraph') write(part.part, item.index, item.text);
    else item.rows.forEach((row) => row.cells.flat().forEach((p) => write(part.part, p.index, p.text)));
  }));
  return { texts, counts };
}

const BUILT_IN = new Set(['FOR', 'END-FOR', 'IF', 'END-IF', 'INS', 'EXEC', 'IMAGE', 'HTML', 'LINK', 'ALIAS']);

/**
 * Whether the text inside braces is a placeholder or command rather than a note such as
 * {provide an example}. The same rule as the server's (word-template.commands.ts).
 */
export function isCommandText(raw: string): boolean {
  const code = raw.trim();
  if (!code) return false;
  if (/^[=!*]/.test(code)) return true;
  const [first, ...others] = code.split(/\s+/);
  const rest = others.join(' ');
  if (BUILT_IN.has(first.toUpperCase())) {
    if (!rest || first === first.toUpperCase() || !/\s/.test(rest)) return true;
    return /^for$/i.test(first) && /^\S+\s+in\s+\S+$/i.test(rest);
  }
  return !/\s/.test(code) && (code.includes('.') || code.startsWith('$'));
}

/** Text and placeholders, for showing placeholders highlighted. Notes in braces stay plain text. */
export function segments(text: string): { text: string; placeholder: boolean }[] {
  return text.split(/(\{[^{}]*\})/).filter(Boolean).map((piece) => ({ text: piece, placeholder: /^\{[^{}]*\}$/.test(piece) && isCommandText(piece.slice(1, -1)) }));
}

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Selected text as a pattern that allows any spacing between its words: wrapped lines, and tabs the layout leaves out. */
const flexible = (text: string) => new RegExp(text.trim().split(/\s+/).map(escapeRegExp).join('\\s*'));

export type LocatedSelection =
  | { kind: 'text'; find: string; matches: { part: string; index: number }[] }
  | { kind: 'paragraphs'; from: number; to: number };

/**
 * Where text selected on the real page is in the document (JT1-1887): the page is a PDF, so the
 * selection is found by its text. Within one paragraph, the text as written there (a note in braces
 * taken whole) and every paragraph it appears in; across paragraphs outside tables, the first and
 * last. Null when it isn't found.
 */
export function locateSelection(parts: WordLayoutPart[], texts: Map<string, string>, selected: string): LocatedSelection | null {
  const lines = selected.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (!lines.length) return null;
  const whole = flexible(lines.join(' '));
  let find: string | null = null;
  Array.from(texts.values()).some((text) => {
    const match = text.match(whole);
    if (match) find = expandToBraces(text, match[0]);
    return !!match;
  });
  if (find) {
    const exact: string = find;
    const matches = Array.from(texts.entries())
      .filter(([, text]) => occurrences(text, exact).length > 0)
      .map(([key]) => ({ part: key.slice(0, key.lastIndexOf(':')), index: Number(key.slice(key.lastIndexOf(':') + 1)) }));
    if (matches.length) return { kind: 'text', find: exact, matches };
  }
  if (lines.length > 1) {
    const where = bodyParagraphs(parts);
    const outside = Array.from(texts.entries())
      .filter(([key]) => key.startsWith('body:'))
      .map(([key, text]) => ({ index: Number(key.slice(5)), text }))
      .filter((p) => where.get(p.index)?.table === undefined);
    const first = outside.find((p) => flexible(lines[0]).test(p.text));
    const last = first && [...outside].reverse().find((p) => p.index >= first.index && flexible(lines[lines.length - 1]).test(p.text));
    if (first && last) return { kind: 'paragraphs', from: first.index, to: last.index };
  }
  return null;
}
